<script setup>
import { computed } from 'vue'
import BaseButton from '../_base-button.vue'
import BaseIcon from '../_base-icon.vue'

// A destructive confirmation (`style: 'danger'` in modal-content.js) gets BaseButton's
// Destructive variant; everything else keeps Primary.
//
// Chosen by prop rather than by a stylesheet override at the call site on purpose: a local
// `.confirm { … }` would have to out-rank BaseButton's own variant selectors, and the variant
// prop is the one treatment the button family guarantees.
const PRIMARY_BUTTON_VARIANTS = {
    danger: 'destructive',
    default: 'primary',
}

const props = defineProps({
    // modal-manager passes `currentModal.data`: the MODAL_CONTENT entry shallow-merged
    // with whatever the caller overrode.
    data: {
        type: Object,
        default: () => ({}),
    },
})

const emit = defineEmits(['close'])

const headline = computed(() => props.data.headline)

const description = computed(() => props.data.description)

// Defaulted, because a render that throws on `primaryAction.text` is worse than a
// useless modal: modal-manager locks body scroll when it opens and only unlocks it on
// `close`, so the member would be left unable to scroll with nothing on screen to dismiss.
const primaryAction = computed(() => props.data.primaryAction || {})

const secondaryAction = computed(() => props.data.secondaryAction)

const primaryButtonVariant = computed(
    () => PRIMARY_BUTTON_VARIANTS[primaryAction.value.style] || PRIMARY_BUTTON_VARIANTS.default
)

function close() {
    emit('close')
}

// The caller does the work the member agreed to once `modalService.show()` resolves
// with 'primary'; every other way out resolves it with nothing.
function handlePrimaryAction() {
    emit('close', 'primary')
}
</script>

<template>
    <div class="confirmation-modal">
        <button
            class="close-button"
            aria-label="Close modal"
            @click="close"
        >
            <BaseIcon
                class="close-icon"
                name="close"
            />
        </button>

        <div class="content">
            <h5 class="headline">{{ headline }}</h5>

            <p
                v-if="description"
                class="description"
            >{{ description }}</p>

            <div class="actions">
                <!-- The click handler has to be bound here: it falls through to BaseButton's own
                     `<button>`, and its absence is what BaseButton's `isLabel` computed reads to
                     apply `base-button-cannot-be-hovered` — `pointer-events: none`. Without it the confirm
                     button is one nothing can press, and every method-level unit test would still
                     pass. -->
                <BaseButton
                    :variant="primaryButtonVariant"
                    @click="handlePrimaryAction"
                >{{ primaryAction.text }}</BaseButton>

                <!-- Cancel is a close and nothing else: only the confirm resolves with 'primary'. -->
                <BaseButton
                    v-if="secondaryAction"
                    variant="tertiary"
                    size="m"
                    @click="close"
                >{{ secondaryAction.text }}</BaseButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// The small-modal shell the other modals in this folder use — narrower than the paywall, since
// a confirmation is a sentence and two buttons.
.confirmation-modal {
    position: relative;
    width: 90vw;
    max-width: 500px;
    padding: $s-xxl;
    border: none;
    border-radius: $size-radius-m;

    @include elevationDialog;

    @include mobileOnly {
        width: calc(100vw - $s-xxl);
        max-width: none;
        padding: $s-xl;
        margin: $s-l;
    }
}

.close-button {
    position: absolute;
    top: $s-l;
    right: $s-l;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: $s-xxs;
    color: $c-modal-close;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 50%;

    @include transition((color, background-color), fast);

    &:hover {
        color: $c-modal-close-hover;
        background: $c-modal-close-hover-bg;
    }
}

// The × is stroked with `currentColor`, so the button's own colour is what paints it.
.close-icon {
    @include square($s-l);
}

.content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    // Clear of the close button, which is absolutely positioned over this corner.
    padding-right: $s-xl;
    text-align: left;
}

.headline {
    @extend %h5;

    margin: 0 0 $s-s;
    color: $c-white;
}

.description {
    @extend %p;

    margin: 0 0 $s-xl;
    color: $c-text-faded;
}

// Confirm first, then the way out. Both buttons hug their labels (BaseButton's default), so
// they wrap rather than stretch if the pair outgrows a narrow modal.
.actions {
    display: flex;
    flex-wrap: wrap;
    gap: $s-s;
    align-items: center;
}
</style>
